import React from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { Box } from '../../../../../../../../CommonComponents/OPL/Box/Box';
import { Dot } from '../../../../../../../../CommonComponents/OPL/Dot/Dot';
import { HtmlTable } from '../../../../../../../../CommonComponents/OPL/HtmlTable/HtmlTable';
import { InputNumberMask } from '../../../../../../../../CommonComponents/OPL/Input/InputNumberMask';
import { Text } from '../../../../../../../../CommonComponents/OPL/Text/Text';
import { TruncatedText } from '../../../../../../../../CommonComponents/OPL/Text/TruncatedText';
import { AssetAllocationDTO } from '../../../../../../api/useAccountsOptionsQuery';
import { FormFields } from '../../AccountForm.meta';
import { useAccountFormContext } from '../../context/useAccountFormContext';
import { AllocationTaxStatusDTO } from '../../../../../../api/useAccountsQuery';

interface Props {
  chartValues: AssetAllocationDTO[];
  assetAllocationTaxStatus: number | null;
  readOnly?: boolean;
}

export const AssetAllocationTable = ({
  chartValues,
  readOnly,
  assetAllocationTaxStatus,
}: Props) => {
  if (!chartValues) {
    return null;
  }

  if (readOnly) {
    return (
      <AssetAllocationTableReadOnlyContent
        chartValues={chartValues}
        assetAllocationTaxStatus={assetAllocationTaxStatus}
      />
    );
  }

  return <AssetAllocationTableFormContent assetAllocationTaxStatus={assetAllocationTaxStatus} />;
};

export const AssetAllocationTableReadOnlyContent = ({
  chartValues,
  assetAllocationTaxStatus,
}: Props) => {
  return (
    <Box css="margin: 0 -24px;">
      <HtmlTable variant="embedded" css="max-height: 265px; overflow-y: auto">
        <table>
          <thead>
            <tr>
              <th css="position: sticky; top: 0; z-index: 10">
                <Text variant="smallCaps" textAlign="left">
                  Name
                </Text>
              </th>

              <th css="position: sticky; top: 0; z-index: 10">
                <Text variant="smallCaps" textAlign="right">
                  Value
                </Text>
              </th>
            </tr>
          </thead>

          <tbody>
            {chartValues.map((field, index) => {
              return (
                <tr key={`${assetAllocationTaxStatus}-${index}`}>
                  <td>
                    <Box display="flex" alignItems="center">
                      {field.color && <Dot bg={field.color} mr="4" />}

                      <TruncatedText fontWeight={500} color="neutral.600">
                        {field.name}
                      </TruncatedText>
                    </Box>
                  </td>

                  <td>
                    <InputNumberMask
                      disabled
                      placeholder="0"
                      value={field.y}
                      rightIcon="far fa-percent"
                      decimalScale={2}
                      allowNegative={false}
                      thousandSeparator={false}
                      sizeVariant="small"
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </HtmlTable>
    </Box>
  );
};

interface AssetAllocationTableFormContentProps {
  assetAllocationTaxStatus: AllocationTaxStatusDTO | null;
}

export const AssetAllocationTableFormContent = ({
  assetAllocationTaxStatus,
}: AssetAllocationTableFormContentProps) => {
  const formBag = useFormContext<FormFields>();

  const { setAlert } = useAccountFormContext();

  const [isLinked, isIntegrated, syncAssetAllocation, assetAllocationList] = formBag.watch([
    'bankAccount.IsLinked',
    'bankAccount.IsIntegrated',
    'bankAccount.SyncAssetAllocationEnabled',
    'accountPortfolio.assetAllocationList',
  ]);

  const assetAllocationIndex = assetAllocationList.findIndex(
    ({ taxStatus }) => taxStatus === assetAllocationTaxStatus,
  );

  const { fields } = useFieldArray({
    name: `accountPortfolio.assetAllocationList.${assetAllocationIndex}.assetAllocation`,
    keyName: 'key',
    control: formBag.control,
  });

  return (
    <Box css="margin: 0 -24px;">
      <HtmlTable variant="embedded" css="max-height: 265px; overflow-y: auto">
        <table>
          <thead>
            <tr>
              <th css="position: sticky; top: 0; z-index: 10">
                <Text variant="smallCaps" textAlign="left">
                  Name
                </Text>
              </th>

              <th css="position: sticky; top: 0; z-index: 10">
                <Text variant="smallCaps" textAlign="right">
                  Value
                </Text>
              </th>
            </tr>
          </thead>

          <tbody>
            {fields.map((field, index) => {
              return (
                <tr key={`${assetAllocationTaxStatus}-${field.id}`}>
                  <td>
                    <Box display="flex" alignItems="center">
                      {field.color && <Dot bg={field.color} mr="4" />}

                      <TruncatedText fontWeight={500} color="neutral.600">
                        {field.name}
                      </TruncatedText>
                    </Box>
                  </td>

                  <td>
                    <Controller
                      control={formBag.control}
                      name={`accountPortfolio.assetAllocationList.${assetAllocationIndex}.assetAllocation.${index}.y`}
                      render={({ field: { onChange, ...rest } }) => {
                        return (
                          <InputNumberMask
                            {...rest}
                            placeholder="0"
                            rightIcon="far fa-percent"
                            defaultValue={field.y}
                            decimalScale={2}
                            allowNegative={false}
                            thousandSeparator={false}
                            sizeVariant="small"
                            onChange={evt => {
                              if ((isLinked || isIntegrated) && syncAssetAllocation) {
                                setAlert({
                                  message:
                                    'Changing your asset allocation will automatically un-sync asset allocation for this account. To re-sync, toggle the sync asset allocation toggle in the sync tab.',
                                  type: 'warning',
                                });

                                formBag.setValue('bankAccount.SyncAssetAllocationEnabled', false);
                              }

                              onChange(evt);
                            }}
                          />
                        );
                      }}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </HtmlTable>
    </Box>
  );
};
